import { useState, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useWatch } from "react-hook-form";
import { Grid, Box } from "@mui/material";

import Form from "components/Form/Form.jsx";
import FormTextField from "components/Form/FormTextField.jsx";
import FormSelect from "components/Form/FormSelect.jsx";
import FormAutocomplete from "components/Form/FormAutocomplete.jsx";
import FormPasswordField from "components/Form/FormPasswordField.jsx";
import FormBlankField from "components/Form/FormBlankField.jsx";
import FormInfoField from "components/Form/FormInfoField.jsx";
import FormDetailedRadioGroup from "components/Form/FormDetailedRadioGroup.jsx";
import SubmitButton from "components/Form/SubmitButton.jsx";
import { passwordHelperText } from "utils/passwordValidation.js";

import {
  userValidationSchema,
  userDefaultValues,
  userOptions,
  optionsApiEndpoints,
  isFieldDisabledByRole,
} from "./AdminUserFormDefinitions.js";

import { backendGet } from "services/dataService.js";

/**
 * Form component for creating or editing a user in the admin section.
 * Uses React Hook Form with Yup validation.
 *
 * @param {object} props - Component props
 * @param {object} props.initialData - Initial data for editing an existing user
 * @param {boolean} props.isEditMode - Whether the form is in edit mode
 * @param {function} props.onSubmit - Function to call when the form is submitted
 * @param {function} props.onCancel - Function to call when the form is cancelled
 * @returns {JSX.Element}
 */
const AdminUserForm = ({
  initialData = {},
  isEditMode = false,
  onSubmit,
  onCancel,
}) => {
  const [kundeOptions, setKundeOptions] = useState([]);

  // Merge initial data with default values
  const defaultValues = {
    ...userDefaultValues,
    ...initialData,
  };

  // Load kunde options on component mount
  useEffect(() => {
    const loadKundeOptions = async () => {
      await backendGet(optionsApiEndpoints.kundeOptions, {
        setResult: setKundeOptions,
      });
    };

    loadKundeOptions();
  }, []);

  const handleSubmit = async (data, formMethods) => {
    await onSubmit(data, formMethods);
  };

  // Role-dependent field rendering
  const RoleDependentFields = () => {
    // Watch the rolle field to conditionally render other fields
    const rolle = useWatch({ name: "rolle" });

    return (
      <>
        {/* Kunde field - conditionally disabled based on role */}
        <Grid item size={{ xs: 12 }}>
          <FormAutocomplete
            name="kunde_id_ov"
            label="Kunde"
            options={kundeOptions}
            disabled={isFieldDisabledByRole("kunde_id_ov", rolle)}
            clearWhenDisabled={true}
          />
        </Grid>
      </>
    );
  };

  return (
    <Form
      defaultValues={defaultValues}
      resolver={yupResolver(userValidationSchema, { context: { isEditMode } })}
      onSubmit={handleSubmit}
      context={{ isEditMode }}
      mode="onChange"
    >
      <Grid container spacing={2}>
        {/* Role and Status */}
        <Grid item size={{ xs: 12, md: 4 }}>
          <FormSelect name="rolle" label="Rolle" options={userOptions.rolle} />
        </Grid>
        <Grid item size={{ xs: 12, md: 4 }}>
          <FormSelect
            name="status"
            label="Status"
            options={userOptions.status}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 4 }}>
          <FormBlankField />
        </Grid>

        {/* Role-dependent fields */}
        <RoleDependentFields />

        {/* Contact Information */}
        <Grid item size={{ xs: 12 }}>
          <FormTextField name="email" label="E-Mail Adresse" fullWidth />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <FormTextField name="telefon" label="Telefon" fullWidth />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <FormTextField name="fax" label="Telefax" fullWidth />
        </Grid>

        {/* Personal Information */}
        <Grid item size={{ xs: 12, md: 4 }}>
          <FormSelect
            name="anrede"
            label="Anrede"
            options={userOptions.anrede}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 4 }}>
          <FormTextField name="titel" label="Titel" fullWidth />
        </Grid>
        <Grid item size={{ xs: 12, md: 4 }}>
          <FormBlankField />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <FormTextField name="vorname" label="Vorname" fullWidth />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <FormTextField name="nachname" label="Nachname" fullWidth />
        </Grid>

        {/* Address */}
        <Grid item size={{ xs: 12 }}>
          <FormTextField name="strasse" label="Straße" fullWidth />
        </Grid>
        <Grid item size={{ xs: 12, md: 4 }}>
          <FormTextField name="plz" label="PLZ" fullWidth />
        </Grid>
        <Grid item size={{ xs: 12, md: 8 }}>
          <FormTextField name="ort" label="Ort" fullWidth />
        </Grid>

        {/* Password */}
        <Grid item size={{ xs: 12, md: 6 }}>
          <FormPasswordField
            name="password"
            label="Passwort"
            autoComplete="new-password"
            helperText={passwordHelperText.requirements}
            fullWidth
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <FormPasswordField
            name="password_repetition"
            label="Passwort (Wiederholung)"
            autoComplete="new-password"
            fullWidth
          />
        </Grid>

        {/* Portal Configuration */}
        <Grid item size={{ xs: 12 }}>
          <FormInfoField label="Konfiguration des Schadenportals" />
        </Grid>

        <Grid item size={{ xs: 12, md: 4 }}>
          <FormSelect
            name="rpp"
            label="Zeilen pro Seite"
            options={userOptions.rpp}
          />
        </Grid>

        <Grid item size={{ xs: 12 }}>
          <FormDetailedRadioGroup
            name="level_benachrichtigung"
            label="Wie möchten Sie über Aktualisierungen im Schadenportal informiert werden?"
            options={userOptions.level_benachrichtigung}
          />
        </Grid>

        {/* Form Actions */}
        <Grid item size={{ xs: 12 }}>
          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}
          >
            {onCancel && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onCancel}
              >
                Abbrechen
              </button>
            )}
            <SubmitButton>
              {isEditMode ? "Speichern" : "Erstellen"}
            </SubmitButton>
          </Box>
        </Grid>
      </Grid>
    </Form>
  );
};

export default AdminUserForm;
